import React, { useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import theme from '../../style/theme'
import { vw } from '../../style/vw'
import { span, sliceMarginStyles } from '../../style/span'
import get from 'lodash/get'
import BadgeElementsBlock from '../RichText/BadgeElementsBlock'
import SplitWordsAnimation from '../SplitWordsAnimation'
import cn from 'classnames'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

const HeadlineSlice = ({ className, slice }) => {
  const { title, page_number: pageNumber, badges } = slice
  const badge = get(badges, [0])
  const classes = useStyles()
  const badgeRef = useRef()
  const sectionRef = useRef()

  useEffect(() => {
    if (badge && badge.element === 'compass' && badgeRef.current) {
      const tl = gsap.timeline()
      const scrollTrigger = ScrollTrigger.create({
        trigger: sectionRef.current,
        pin: false,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 2,
        animation: tl
      })
      tl.to(badgeRef.current.children[0], { rotate: '360deg' })
      return () => {
        tl.kill()
        scrollTrigger.kill()
      }
    }
  }, [badge])

  useEffect(() => {
    gsap.to(badgeRef.current, { y: 0, duration: 1.8, ease: 'expo.out' })
    gsap.to(badgeRef.current.children, { y: 0, duration: 1.8, ease: 'expo.out' })
    gsap.to(badgeRef.current.children, { scale: 1, duration: 1.4, ease: 'sine.out' })
  }, [])

  return (
    <section ref={sectionRef} className={cn(classes.section, pageNumber && 'withPageNumber')}>
      {pageNumber && <span className={classes.pageNumber}>{pageNumber}</span>}
      <SplitWordsAnimation>
        <h3 className={cn(classes.title, pageNumber && 'withPageNumber')}>
          {title}
          {badge && <BadgeElementsBlock ref={badgeRef} className={classes.icon} node={badge} />}
        </h3>
      </SplitWordsAnimation>
    </section>
  )
}

const useStyles = createUseStyles({
  section: {
    marginBottom: vw(28),
    position: 'relative',
    marginLeft: span(1),
    marginRight: span(1),
    [theme.breakpoints.up('md')]: {
      marginBottom: vw(56, 'desktop'),
      marginLeft: span(1, 'md'),
      marginRight: span(1, 'md')
    },
    '&.withPageNumber': {
      extend: [sliceMarginStyles],
      marginLeft: span(1),
      [theme.breakpoints.up('md')]: {
        marginLeft: span(2, 'md')
      }
    }
  },
  title: {
    fontSize: vw(54),
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    lineHeight: 1,
    whiteSpace: 'pre-wrap',
    width: vw(300),
    [theme.breakpoints.up('md')]: {
      fontSize: vw(100, 'desktop'),
      width: vw(780, 'desktop')
    },
    '& p': {
      display: 'inline'
    },
    '&.withPageNumber': {
      fontSize: vw(48),
      [theme.breakpoints.up('md')]: {
        fontSize: vw(160, 'desktop'),
        width: span(18, 'md')
      }
    }
  },
  pageNumber: {
    display: 'none',
    position: 'absolute',
    left: 0,
    top: 0,
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    fontSize: vw(24),
    [theme.breakpoints.up('md')]: {
      left: span(-1, 'md'),
      display: 'block',
      fontSize: vw(24, 'desktop')
    }
  },
  icon: {
    float: 'left',
    marginRight: '0.15em',
    [theme.breakpoints.up('md')]: {
      padding: [0, 0, '0.05em']
    }
  }
}, { name: 'HeadlineSlice' })

export default HeadlineSlice
